<template>
  <w-modal
    v-model="show"
    title="Generowanie dokumentów"
    class="modal-big"
    :show-close-button="false"
    :close-on-submit="false"
    :hide-on-overlay="false"
    :hide-on-esc="false"
    @submit="$emit('close')"
    @close="$emit('close')"
  >
    <div class="row mb-2">
      <div class="form-group col-12">
        Do wygenerowania dokumentu zostaną użyte zaznaczone dokumenty.
      </div>

      <div class="form-group col-12">
        <select id="view" v-model="selectedView" class="form-control">
          <option v-for="view in views" :key="'view_' + view.id" :value="view.id">
            {{ view.name }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="view" class="row mb-2">
      <div class="form-group col-12">
        Wygeneruj dokument do:
      </div>

      <div class="form-group col-12">
        <w-radio
          class="d-inline-block mr-2"
          title="dokumentacji procedury"
          :model="$schema.target"
          :value="GenerateTarget.PROCEDURE"
        />

        <w-radio
          v-if="!view.singleView"
          class="d-inline-block mr-2"
          title="dokumentacji wniosku"
          :model="$schema.target"
          :value="GenerateTarget.DOCUMENT"
        />
      </div>
    </div>

    <div v-if="$schema.target.$model === GenerateTarget.PROCEDURE">
      <w-input
        :model="$schema.caseId"
        @input="loadDocumentName($schema.caseId.$model, 'mdok/getCaseName', 'mdok/saveCaseName')"
      />

      <p v-if="caseName">
        Wybrano sprawę: <b>{{ caseName }}</b>
      </p>
    </div>

    <schema-form
      v-if="loadedSchema && schema"
      class="mt-4"
      :template-name="$schema.$title"
      :schema="$schema"
      :documents="documents"
    />

    <div slot="footer" class="w-100">
      <w-button class="btn btn-secondary float-left mr-2" text="Zamknij" @click="close" />

      <div class="float-right">
        <w-button
          v-if="mdokEnabled && view"
          class="btn-primary mr-2"
          text="Prześlij do MDOK"
          :disabled="disableButtons"
          @click="sendToMDOK"
        />

        <w-button
          v-if="view"
          class="btn-primary mr-2"
          text="Zapisz w systemie"
          :disabled="disableButtons"
          @click="saveAsAttachment"
        />

        <post-form
          v-if="pdfUrl"
          text="Pobierz pdf"
          icon="fal fa-cloud-download mr-2"
          button-class="btn-primary"
          :disabled="disableButtons"
          :fields="postData"
          :url="pdfUrl"
        />
      </div>
    </div>
  </w-modal>
</template>

<script>
import SchemaForm from '@/components/forms/SchemaForm';
import PostForm from '@/components/postForm/PostForm';
import { Errors } from '@/enums/errors.enum';
import { GenerateTarget } from '@/enums/generateTarget.enum';
import { Successes } from '@/enums/successes.enum';
import { formMixin } from '@/mixins/form';
import { generateMixin } from '@/mixins/generate';
import { mdokDocumentMixin } from '@/mixins/mdokDocument';
import { notificationsMixin } from '@/mixins/notifications';
import schema from '@/schemas/procedureGenerate.schema';
import { config } from '@/utils/config';
import { tryParseInt } from '@/utils/number';
import { showErrorsDialog } from '@/utils/showErrorsDialog';

// @vuese
// Modal wyboru statusu
// @group
export default {
  components: { PostForm, SchemaForm },
  mixins: [formMixin, generateMixin, mdokDocumentMixin, notificationsMixin],
  props: {
    // Czy modal ma być widoczny
    show: { type: Boolean, required: true },
    // Filtry dokumentów
    filters: { type: Array, required: true },
    // Dokumenty
    documents: { type: Array, required: true }
  },
  schema,
  data() {
    return { model: {}, isUploading: false, mdokEnabled: config.mdokEnabled, GenerateTarget };
  },

  computed: {
    // Aktalnie wybrany widok
    view() {
      return this.selectedView && this.views.find(view => view.id === this.selectedView);
    },
    // URL do generowania PDF
    pdfUrl() {
      return (
        this.view &&
        config.renderURL +
          `/pdf/documents/${this.view.id}?procedureId=${this.procedureId}${
            this.view.singleView ? '&singleView=true' : ''
          }`
      );
    },
    // Dane formularza generującego PDF
    postData() {
      return [...this.viewFormFields, ...this.filters];
    },
    procedureId() {
      return this.$route.params.procedureId.toString();
    },
    disableButtons() {
      return this.postFormsDisabled || this.isUploading;
    },
    filtersAsObject() {
      const filterObject = {};
      this.filters.forEach(element => {
        filterObject[element.name] = element.value;
      });

      return filterObject;
    }
  },
  watch: {
    documents() {
      this.setReceipients();
    },
    show() {
      this.$schema.caseId.$setData(null);
      this.clearMdok();
    }
  },
  methods: {
    // @vuese
    // Ładuje schemę
    loadSchema() {
      this.model = {};
      this.$external.documents = this.documents;

      if (this.view?.data?.multipleSchemaId) {
        return this.$store.dispatch('schema/get', this.view?.data?.multipleSchemaId).then(loadedSchema => {
          const multipleSchema = JSON.parse(loadedSchema.schema);

          this.$setSchema({ allOf: [multipleSchema, schema] });
          this.setReceipients();
          this.clearMdok();
          this.loadedSchema = true;
        });
      } else if (this.schema) {
        this.$setSchema({ allOf: [this.schema, schema] });
      } else {
        this.$setSchema(schema);
      }
      this.setReceipients();
      this.clearMdok();
      this.loadedSchema = true;
    },

    // @vuese
    // Czyści pobraną sprawę mdok
    clearMdok() {
      this.$store.commit('mdok/saveCaseName', null);
    },

    // @vuese
    // Ustawianie odbiorców dokumentów
    setReceipients() {
      if (this.documents && this.$schema.recipients) {
        this.$schema.recipients.$setData(
          this.documents.map(doc => ({
            ...doc,
            ...doc.data,
            student: doc.data.tenderer,
            tenderer: doc.data.applicant,
            address: doc.data.applicant.address
          })),
          undefined,
          true
        );
      }
    },
    // @vuese
    // Zapisuje widok jako załącznik
    saveAsAttachment() {
      const data = this.$schema.$getData();
      if (data.target === GenerateTarget.PROCEDURE) {
        return this.save('view/saveGroupAsAttachment');
      }
      return this.sendToDocuments('view/saveAsAttachment', data);
    },
    // @vuese
    // Zapisuje widok jako załącznik i przesyła do MDOk
    sendToMDOK() {
      const data = this.$schema.$getData();
      if (data.target === GenerateTarget.PROCEDURE) {
        return this.save('view/sendGroupToMDOK', true);
      }
      return this.sendToDocuments('view/sendToMDOK', data);
    },
    // @vuese
    // Zapisuje widok używając odpowiedniej akcji
    save(action, withCaseId) {
      this.isUploading = true;
      const data = this.$schema.$getData();
      return this.$store
        .dispatch(action, {
          caseId: withCaseId ? data.caseId : undefined,
          viewId: this.view.id,
          procedureId: tryParseInt(this.procedureId),
          data: { ...data, ...this.filtersAsObject },
          singleView: this.view.singleView
        })
        .then(() => {
          this.notifySuccess(Successes.ATTACHMENT_SAVED);
        })
        .catch(err => this.notifyError(Errors.ERROR_TRY_AGAIN_LATER))
        .finally(() => (this.isUploading = false));
    },
    // @vuese
    // Wysłanie danych do wielu dokumentów
    async sendToDocuments(action, data) {
      const errors = [];
      if (data.target) {
        delete data.target;
      }
      let recipients = [];
      if (data.recipients) {
        recipients = data.recipients;
        delete data.recipients;
      }

      this.isUploading = true;
      for (const document of this.documents) {
        const documentId = document.documentId;
        const documentViewData = { documentId, data: { ...data }, viewId: this.view.id };
        if (recipients.length) {
          const recipient = recipients.find(recipientItem => recipientItem.documentId === documentId);
          documentViewData.data.recipient = recipient;
        }
        await this.$store
          .dispatch(action, documentViewData)
          .catch(error => errors.push({ entityId: documentId, error }));
      }
      this.isUploading = false;

      if (!errors.length) {
        return this.notifySuccess(Successes.ATTACHMENTS_SAVED);
      }
      showErrorsDialog(this.$store, Errors.ATTACHMENTS_CANNOT_SAVE, errors);
    }
  }
};
</script>
